import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  IconButton,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Pagination,
} from '@mui/material';
import { SvgIconProps } from '@mui/material/SvgIcon';
import FlightTakeoffIcon from '@mui/icons-material/FlightTakeoff';
import DirectionsBoatIcon from '@mui/icons-material/DirectionsBoat';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import dayjs from 'dayjs';

import {
  SUPPLY_HUB_SHIPPING_MODE_CONFIG,
  SupplyHubDrawerContext,
  SupplyHubShippingMode,
  SupplyHubVendorGroup,
} from './SupplyHubTypes';
import { formatCurrency, handleEntityLinkClick } from '../utils';
import type { SupplyHubPurchaseOrder } from '../../../types';
import type { SupplyHubShippingSummary } from './SupplyHubTypes';

type ShippingTotals = Record<SupplyHubShippingMode, { count: number; value: number }>;

type SupplyHubStats = {
  vendorCount: number;
  orderCount: number;
  totalValue: number;
  shippingMode?: SupplyHubShippingMode;
};

interface SupplyHubLegendProps {
  shippingTotals: ShippingTotals;
  totalPurchaseOrders: number;
  warehouseCount: number;
  vendorCount: number;
  activeMode: 'all' | SupplyHubShippingMode;
  onModeChange: (mode: 'all' | SupplyHubShippingMode) => void;
}

const renderShippingIcon = (mode: SupplyHubShippingMode, props?: SvgIconProps) => {
  switch (mode) {
    case 'air':
      return <FlightTakeoffIcon fontSize="small" {...props} />;
    case 'sea':
      return <DirectionsBoatIcon fontSize="small" {...props} />;
    case 'truck':
      return <LocalShippingIcon fontSize="small" {...props} />;
    case 'pickup':
      return <Inventory2Icon fontSize="small" {...props} />;
    default:
      return <HelpOutlineIcon fontSize="small" {...props} />;
  }
};

const ShippingBadge: React.FC<{ mode: SupplyHubShippingMode; count?: number }> = ({ mode, count }) => {
  const config = SUPPLY_HUB_SHIPPING_MODE_CONFIG[mode];
  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 1,
        fontSize: 12,
        fontWeight: 600,
        color: '#0f172a',
      }}
    >
      <Box
        sx={{
          width: 22,
          height: 22,
          borderRadius: '50%',
          backgroundColor: config.color,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#ffffff',
        }}
      >
        {renderShippingIcon(mode, { fontSize: 'inherit', sx: { fontSize: 16 } })}
      </Box>
      <span>
        {config.label}
        {typeof count === 'number' ? ` (${count})` : ''}
      </span>
    </Box>
  );
};

const formatLocation = (parts: Array<string | undefined | null>): string =>
  parts
    .map((part) => (part || '').trim())
    .filter((part) => part.length > 0)
    .join(', ');

const formatPurchaseDate = (value: string | undefined | null): string => {
  if (!value) return 'Unknown date';
  const parsed = dayjs(value);
  if (!parsed.isValid()) return value;
  return parsed.format('MMM D, YYYY');
};

const formatShipMethod = (method: string | undefined | null): string => {
  if (!method) return 'Not specified';
  return method.trim();
};

interface VendorGroupCardProps {
  group: SupplyHubVendorGroup;
  orders: SupplyHubPurchaseOrder[];
  expandedOrders: Record<string, boolean>;
  onToggleOrder: (orderId: string) => void;
  shippingSummary?: SupplyHubShippingSummary;
  showShippingSummary?: boolean;
}

const VendorGroupCard: React.FC<VendorGroupCardProps> = ({
  group,
  orders,
  expandedOrders,
  onToggleOrder,
  shippingSummary,
  showShippingSummary = true,
}) => {
  const summary = shippingSummary ?? group.shippingSummary;
  const effectiveOrders = useMemo(
    () => orders.filter((order): order is SupplyHubPurchaseOrder => Boolean(order)),
    [orders],
  );

  if (effectiveOrders.length === 0) {
    return null;
  }

  const locationLabel = formatLocation([group.location.city, group.location.state, group.location.country]);

  return (
    <Box
      sx={{
        borderRadius: 2,
        border: '1px solid rgba(15,23,42,0.1)',
        backgroundColor: '#ffffff',
        padding: 2,
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 1,
          }}
        >
          <Box sx={{ fontSize: 15, fontWeight: 700, color: '#0f172a' }}>
            {group.vendorId ? (
              <Box
                component="a"
                href="#"
                onClick={(event: React.MouseEvent<HTMLAnchorElement>) =>
                  handleEntityLinkClick(event, String(group.vendorId))
                }
                sx={{
                  color: '#0f172a',
                  textDecoration: 'none',
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                {group.vendorName}
              </Box>
            ) : (
              group.vendorName
            )}
          </Box>
        </Box>
        <Box sx={{ fontSize: 12, color: '#475569' }}>{locationLabel}</Box>
        <Box sx={{ fontSize: 12, color: '#64748b' }}>
          {effectiveOrders.length} {effectiveOrders.length === 1 ? 'Purchase Order' : 'Purchase Orders'}
        </Box>
        {showShippingSummary && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {(Object.keys(SUPPLY_HUB_SHIPPING_MODE_CONFIG) as SupplyHubShippingMode[]).map((mode) => {
              const modeSummary = summary[mode];
              if (!modeSummary || modeSummary.count === 0) return null;
              return <ShippingBadge key={mode} mode={mode} count={modeSummary.count} />;
            })}
          </Box>
        )}
      </Box>

      <Box
        component="table"
        sx={{
          width: '100%',
          borderCollapse: 'collapse',
          fontSize: 12,
          border: '1px solid rgba(148,163,184,0.4)',
        }}
      >
        <Box component="thead" sx={{ backgroundColor: 'rgba(226,232,240,0.4)', textAlign: 'left' }}>
          <Box component="tr">
            <Box component="th" sx={{ width: 40, padding: 1 }} />
            <Box component="th" sx={{ padding: 1 }}>PO #</Box>
            <Box component="th" sx={{ padding: 1 }}>Purchase Date</Box>
            <Box component="th" sx={{ padding: 1 }}>Payment Term</Box>
            <Box component="th" sx={{ padding: 1 }}>Ship Method</Box>
            <Box component="th" sx={{ padding: 1, textAlign: 'right' }}>Total</Box>
          </Box>
        </Box>
        <Box component="tbody">
          {effectiveOrders.map((order) => {
            const orderId = order.purchase_id ?? 0;
            const isExpanded = expandedOrders[orderId] ?? false;
            const shippingMode = (Object.keys(group.shippingSummary) as SupplyHubShippingMode[]).find((mode) =>
              group.shippingSummary[mode]?.orders?.some((candidate) => candidate.purchase_id === orderId),
            ) as SupplyHubShippingMode | undefined;
            return (
              <React.Fragment key={orderId}>
                <Box
                  component="tr"
                  sx={{
                    cursor: 'pointer',
                    backgroundColor: isExpanded ? 'rgba(59,130,246,0.05)' : undefined,
                    '&:nth-of-type(odd)': {
                      backgroundColor: isExpanded ? 'rgba(59,130,246,0.08)' : 'rgba(248,250,252,0.8)',
                    },
                    transition: 'background-color 0.2s ease',
                    borderBottom: '1px solid rgba(148,163,184,0.3)',
                  }}
                  onClick={() => onToggleOrder(orderId)}
                >
                  <Box component="td" sx={{ padding: 0, textAlign: 'center' }}>
                    <IconButton
                      aria-label="Toggle purchase order"
                      size="small"
                      onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        onToggleOrder(orderId);
                      }}
                      aria-expanded={isExpanded}
                      sx={{
                        transition: 'transform 0.2s ease',
                        transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                      }}
                    >
                      <KeyboardArrowRightIcon fontSize="small" />
                    </IconButton>
                  </Box>
                  <Box component="td" sx={{ padding: 1, fontWeight: 600, color: '#0f172a' }}>
                    {orderId}
                  </Box>
                  <Box component="td" sx={{ padding: 1, color: '#475569' }}>
                    {formatPurchaseDate(order.purchase_date)}
                  </Box>
                  <Box component="td" sx={{ padding: 1, color: '#475569' }}>
                    {order.payment_term || 'N/A'}
                  </Box>
                  <Box component="td" sx={{ padding: 1 }}>
                    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, color: '#475569' }}>
                      {renderShippingIcon(shippingMode ?? 'other', {
                        sx: { fontSize: 18, color: SUPPLY_HUB_SHIPPING_MODE_CONFIG[shippingMode ?? 'other'].color },
                      })}
                      <span>{formatShipMethod(order.ship_method)}</span>
                    </Box>
                  </Box>
                  <Box component="td" sx={{ padding: 1, textAlign: 'right', fontWeight: 600, color: '#0b8043' }}>
                    {formatCurrency(Number(order.total) || 0)}
                  </Box>
                </Box>
                {isExpanded && (
                  <Box component="tr">
                    <Box component="td" colSpan={6} sx={{ padding: 0 }}>
                      <Box
                        component="table"
                        sx={{
                          width: '100%',
                          borderCollapse: 'collapse',
                          fontSize: 12,
                          borderTop: '1px solid rgba(148,163,184,0.4)',
                          backgroundColor: '#ffffff',
                        }}
                      >
                        <Box component="thead" sx={{ backgroundColor: 'rgba(226,232,240,0.35)', textAlign: 'left' }}>
                          <Box component="tr">
                            <Box component="th" sx={{ padding: 1 }}>Item</Box>
                            <Box component="th" sx={{ padding: 1 }}>Description</Box>
                            <Box component="th" sx={{ padding: 1 }}>UOM</Box>
                            <Box component="th" sx={{ padding: 1 }}>Status</Box>
                            <Box component="th" sx={{ padding: 1 }}>Ship Date</Box>
                            <Box component="th" sx={{ padding: 1 }}>Origin</Box>
                          </Box>
                        </Box>
                        <Box component="tbody">
                          {(order.items || []).map((item, index) => (
                            <Box
                              component="tr"
                              key={`${orderId}-${index}`}
                              sx={{ '&:nth-of-type(odd)': { backgroundColor: 'rgba(249,250,251,0.6)' } }}
                            >
                              <Box component="td" sx={{ padding: 1, verticalAlign: 'top' }}>
                                {item.description || 'N/A'}
                              </Box>
                              <Box component="td" sx={{ padding: 1, verticalAlign: 'top' }}>
                                {item.long_description || 'N/A'}
                              </Box>
                              <Box component="td" sx={{ padding: 1, verticalAlign: 'top' }}>
                                {item.stock_uom || 'N/A'}
                              </Box>
                              <Box component="td" sx={{ padding: 1, verticalAlign: 'top' }}>
                                {item.item_ship_status || 'N/A'}
                              </Box>
                              <Box component="td" sx={{ padding: 1, verticalAlign: 'top' }}>
                                {formatPurchaseDate(item.ship_date)}
                              </Box>
                              <Box component="td" sx={{ padding: 1, verticalAlign: 'top' }}>
                                {item.country_of_origin || 'N/A'}
                              </Box>
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                )}
              </React.Fragment>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
};

export const SupplyHubLegend: React.FC<SupplyHubLegendProps> = ({ shippingTotals, totalPurchaseOrders, warehouseCount, vendorCount, activeMode, onModeChange }) => {
  const shippingModes = Object.keys(SUPPLY_HUB_SHIPPING_MODE_CONFIG) as SupplyHubShippingMode[];

  const modeChips: Array<{ key: 'all' | SupplyHubShippingMode; label: string; count: number; color?: string }> = [
    { key: 'all', label: 'All', count: totalPurchaseOrders },
    ...shippingModes.map((mode) => ({
      key: mode,
      label: SUPPLY_HUB_SHIPPING_MODE_CONFIG[mode].label,
      count: shippingTotals[mode]?.count ?? 0,
      color: SUPPLY_HUB_SHIPPING_MODE_CONFIG[mode].color,
    })),
  ];

  const allTooltip = `Warehouses ${warehouseCount.toLocaleString()} • Vendors ${vendorCount.toLocaleString()} • Orders ${totalPurchaseOrders.toLocaleString()}`;

  return (
    <Box
      sx={{
        position: 'fixed',
        left: 16,
        bottom: 88,
        zIndex: 1000,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          padding: '8px 16px',
          backgroundColor: '#ffffff',
          borderRadius: '999px',
          boxShadow: '0 12px 28px rgba(15,23,42,0.15)',
          border: '1px solid rgba(148,163,184,0.3)',
        }}
      >
        {modeChips.map((chip) => {
          const isActive = activeMode === chip.key;
          const chipColor = chip.color ?? '#0f172a';
          const disabled = chip.key !== 'all' && chip.count === 0;

          return (
            <Box
              key={chip.key}
              component="button"
              type="button"
              onClick={() => !disabled && onModeChange(chip.key)}
              title={chip.key === 'all' ? allTooltip : SUPPLY_HUB_SHIPPING_MODE_CONFIG[chip.key as SupplyHubShippingMode].label}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: 36,
                height: 36,
                borderRadius: '999px',
                border: isActive ? `1px solid ${chipColor}` : '1px solid rgba(148,163,184,0.35)',
                backgroundColor: '#ffffff',
                color: isActive ? chipColor : '#0f172a',
                cursor: disabled ? 'not-allowed' : 'pointer',
                opacity: disabled ? 0.4 : 1,
                transition: 'all 0.2s ease',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: isActive ? chipColor : '#475569' }}>
                {chip.key === 'all' ? (
                  <>
                    <Typography component="span" sx={{ fontSize: 12, fontWeight: 700, color: '#0f172a' }}>
                      All
                    </Typography>
                    <Typography component="span" sx={{ fontSize: 11 }}>
                      {chip.count.toLocaleString()}
                    </Typography>
                  </>
                ) : (
                  <>
                    {renderShippingIcon(chip.key as SupplyHubShippingMode, {
                      sx: { fontSize: 18, color: isActive ? chipColor : '#475569' },
                    })}
                    <Typography component="span" sx={{ fontSize: 12, fontWeight: 600 }}>
                      {chip.label}
                    </Typography>
                    <Typography component="span" sx={{ fontSize: 11 }}>
                      {chip.count.toLocaleString()}
                    </Typography>
                  </>
                )}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

interface SupplyHubDrawerContentProps {
  context: SupplyHubDrawerContext;
  shippingModeFilter: 'all' | SupplyHubShippingMode;
}

export const SupplyHubDrawerContent: React.FC<SupplyHubDrawerContentProps> = ({ context, shippingModeFilter }) => {
  const [expandedOrders, setExpandedOrders] = useState<Record<string, boolean>>({});
  const [vendorSearch, setVendorSearch] = useState('');
  const [vendorPage, setVendorPage] = useState(1);
  const [vendorPageSize, setVendorPageSize] = useState(5);

  const contextKey = useMemo(() => {
    if (!context) return 'none';
    if (context.type === 'warehouse') {
      const key = context.warehouse.warehouse_id ?? context.warehouse.warehouse_name ?? 'warehouse';
      return `warehouse-${key}-${shippingModeFilter}`;
    }
    if (context.type === 'vendor') {
      const key = context.vendorGroup.vendorId ?? context.vendorGroup.key;
      return `vendor-${key}-${shippingModeFilter}`;
    }
    const key = context.vendorGroup.vendorId ?? context.vendorGroup.key;
    return `route-${key}-${context.shippingMode}-${shippingModeFilter}`;
  }, [context, shippingModeFilter]);

  useEffect(() => {
    setExpandedOrders({});
  }, [contextKey]);

  useEffect(() => {
    setVendorSearch('');
    setVendorPage(1);
  }, [contextKey]);

  useEffect(() => {
    setVendorPage(1);
  }, [vendorPageSize]);

  const toggleOrder = (orderId: string) => {
    setExpandedOrders((prev) => ({
      ...prev,
      [orderId]: !prev[orderId],
    }));
  };

  const stats = useMemo<SupplyHubStats | null>(() => {
    if (!context) {
      return null;
    }

    if (context.type === 'warehouse') {
      const totalValue = context.vendorGroups.reduce((acc, group) => acc + (group.totalValue || 0), 0);
      return {
        vendorCount: context.vendorGroups.length,
        orderCount: context.totalPurchaseOrders,
        totalValue,
      };
    }

    if (context.type === 'vendor') {
      const totalValue = (
        context.vendorGroup.totalValue ||
        context.vendorGroup.purchaseOrders.reduce((acc, order) => acc + (Number(order.total) || 0), 0)
      );

      return {
        vendorCount: 1,
        orderCount: context.vendorGroup.totalOrders,
        totalValue,
      };
    }

    const totalValue = context.orders.reduce((acc, order) => acc + (Number(order.total) || 0), 0);
    return {
      vendorCount: 1,
      orderCount: context.orders.length,
      totalValue,
      shippingMode: context.shippingMode,
    };
  }, [context]);

  const renderStats = (statsData: SupplyHubStats | null) => {
    if (!statsData) {
      return null;
    }

    const shippingMode = statsData.shippingMode;

    return (
      <Box
        sx={{
          display: 'grid',
          gap: 1.5,
          gridTemplateColumns: {
            xs: 'repeat(1, minmax(0, 1fr))',
            sm: 'repeat(2, minmax(0, 1fr))',
            md: shippingMode ? 'repeat(4, minmax(0, 1fr))' : 'repeat(3, minmax(0, 1fr))',
          },
        }}
      >
        <Box
          sx={{
            borderRadius: 2,
            padding: 2,
            border: '1px solid rgba(148,163,184,0.4)',
            backgroundColor: '#f8fafc',
            display: 'flex',
            flexDirection: 'column',
            gap: 0.5,
          }}
        >
          <Typography variant="caption" sx={{ color: '#475569', fontWeight: 600 }}>
            Vendors
          </Typography>
          <Typography variant="h6" sx={{ color: '#0f172a', fontWeight: 700 }}>
            {statsData.vendorCount.toLocaleString()}
          </Typography>
        </Box>

        <Box
          sx={{
            borderRadius: 2,
            padding: 2,
            border: '1px solid rgba(148,163,184,0.4)',
            backgroundColor: '#f1f5f9',
            display: 'flex',
            flexDirection: 'column',
            gap: 0.5,
          }}
        >
          <Typography variant="caption" sx={{ color: '#475569', fontWeight: 600 }}>
            Purchase Orders
          </Typography>
          <Typography variant="h6" sx={{ color: '#0f172a', fontWeight: 700 }}>
            {statsData.orderCount.toLocaleString()}
          </Typography>
        </Box>

        <Box
          sx={{
            borderRadius: 2,
            padding: 2,
            border: '1px solid rgba(148,163,184,0.4)',
            backgroundColor: '#fff7ed',
            display: 'flex',
            flexDirection: 'column',
            gap: 0.5,
          }}
        >
          <Typography variant="caption" sx={{ color: '#92400e', fontWeight: 600 }}>
            Order Value
          </Typography>
          <Typography variant="h6" sx={{ color: '#b45309', fontWeight: 700 }}>
            {formatCurrency(statsData.totalValue)}
          </Typography>
        </Box>

        {shippingMode && (
          <Box
            sx={{
              borderRadius: 2,
              padding: 2,
              border: '1px solid rgba(148,163,184,0.4)',
              backgroundColor: '#eef2ff',
              display: 'flex',
              flexDirection: 'column',
              gap: 0.5,
            }}
          >
            <Typography variant="caption" sx={{ color: '#4338ca', fontWeight: 600 }}>
              Shipping Mode
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#312e81', fontWeight: 700 }}>
              {renderShippingIcon(shippingMode, {
                sx: { fontSize: 20, color: SUPPLY_HUB_SHIPPING_MODE_CONFIG[shippingMode].color },
              })}
              <span>{SUPPLY_HUB_SHIPPING_MODE_CONFIG[shippingMode].label}</span>
            </Box>
          </Box>
        )}
      </Box>
    );
  };

  const warehouseVendorGroups = context?.type === 'warehouse' ? context.vendorGroups : [];

  const filteredVendorGroups = useMemo(() => {
    if (!context || context.type !== 'warehouse') {
      return [];
    }
    const normalized = vendorSearch.trim().toLowerCase();
    if (!normalized) {
      return context.vendorGroups;
    }
    return context.vendorGroups.filter((group) => {
      const haystack = [
        group.vendorName,
        group.location.city,
        group.location.state,
        group.location.country,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(normalized);
    });
  }, [context, vendorSearch]);

  const vendorTotalPages = context?.type === 'warehouse'
    ? Math.max(1, Math.ceil(filteredVendorGroups.length / vendorPageSize))
    : 1;

  useEffect(() => {
    if (context?.type !== 'warehouse') {
      return;
    }
    if (vendorPage > vendorTotalPages) {
      setVendorPage(vendorTotalPages);
    }
  }, [context, vendorPage, vendorTotalPages]);

  const paginatedVendorGroups = useMemo(() => {
    if (context?.type !== 'warehouse') {
      return [];
    }
    const start = (vendorPage - 1) * vendorPageSize;
    return filteredVendorGroups.slice(start, start + vendorPageSize);
  }, [context, filteredVendorGroups, vendorPage, vendorPageSize]);

  const vendorRangeStart = filteredVendorGroups.length === 0 ? 0 : (vendorPage - 1) * vendorPageSize + 1;
  const vendorRangeEnd = filteredVendorGroups.length === 0
    ? 0
    : Math.min(filteredVendorGroups.length, vendorRangeStart + paginatedVendorGroups.length - 1);

  if (!context) {
    return null;
  }

  if (context.type === 'warehouse') {
    const warehouse = context.warehouse;
    const warehouseLocation = formatLocation([
      warehouse.warehouse_name,
      warehouse.city,
      warehouse.state,
      warehouse.country,
    ]);

    const vendorSummaryLabel = filteredVendorGroups.length === 0
      ? 'No vendors match your filters.'
      : `Showing ${vendorRangeStart}-${vendorRangeEnd} of ${filteredVendorGroups.length}${vendorSearch ? ` (filtered from ${warehouseVendorGroups.length})` : ''} vendors.`;

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {renderStats(stats)}
        <Box sx={{ fontSize: 16, fontWeight: 700, color: '#0f172a' }}>{warehouseLocation}</Box>
        <Box sx={{ fontSize: 13, color: '#475569' }}>
          Total Purchase Orders: <strong>{context.totalPurchaseOrders}</strong>
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1.5,
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <TextField
            size="small"
            label="Search vendors"
            variant="outlined"
            value={vendorSearch}
            onChange={(event) => {setVendorSearch(event.target.value);setVendorPage(1);}}
            sx={{ flex: '1 1 220px', minWidth: 200 }}
          />
          <FormControl size="small" sx={{ width: 140 }}>
            <InputLabel id={`${contextKey}-vendor-entries-label`}>Entries</InputLabel>
            <Select
              labelId={`${contextKey}-vendor-entries-label`}
              id={`${contextKey}-vendor-entries`}
              value={vendorPageSize}
              label="Entries"
              onChange={(event) => setVendorPageSize(Number(event.target.value))}
            >
              <MenuItem value={5}>5</MenuItem>
              <MenuItem value={10}>10</MenuItem>
              <MenuItem value={25}>25</MenuItem>
            </Select>
          </FormControl>
        </Box>

        <Typography variant="caption" sx={{ color: '#64748b' }}>
          {vendorSummaryLabel}
        </Typography>

        {paginatedVendorGroups.length === 0 ? (
          <Box sx={{ fontSize: 13, color: '#64748b' }}>No vendors to display.</Box>
        ) : (
          paginatedVendorGroups.map((group) => (
            <VendorGroupCard
              key={group.key}
              group={group}
              orders={group.purchaseOrders}
              shippingSummary={group.shippingSummary}
              expandedOrders={expandedOrders}
              onToggleOrder={toggleOrder}
            />
          ))
        )}

        {vendorTotalPages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Pagination
              count={vendorTotalPages}
              page={vendorPage}
              onChange={(_, value) => setVendorPage(value)}
              size="small"
              color="primary"
              showFirstButton
              showLastButton
            />
          </Box>
        )}
      </Box>
    );
  }

  if (context.type === 'vendor') {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {renderStats(stats)}
        <VendorGroupCard
          group={context.vendorGroup}
          orders={context.vendorGroup.purchaseOrders}
          shippingSummary={context.vendorGroup.shippingSummary}
          expandedOrders={expandedOrders}
          onToggleOrder={toggleOrder}
        />
      </Box>
    );
  }

  if (context.type === 'route') {
    const routeSummary: SupplyHubShippingSummary = {
      air: { count: 0, orders: [], totalValue: 0 },
      sea: { count: 0, orders: [], totalValue: 0 },
      truck: { count: 0, orders: [], totalValue: 0 },
      pickup: { count: 0, orders: [], totalValue: 0 },
      other: { count: 0, orders: [], totalValue: 0 },
    };

    routeSummary[context.shippingMode] = {
      count: context.orders.length,
      orders: context.orders,
      totalValue: context.orders.reduce((acc, order) => acc + (Number(order.total) || 0), 0),
    };

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {renderStats(stats)}
        <VendorGroupCard
          group={context.vendorGroup}
          orders={context.orders}
          shippingSummary={routeSummary}
          expandedOrders={expandedOrders}
          onToggleOrder={toggleOrder}
          showShippingSummary={false}
        />
      </Box>
    );
  }

  return null;
};


